import { CELL, GROUND } from '../core/config';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import { key, type Building } from '../world/city';

interface Ceiling {
  building: Building;
  row: number;
  version: number;
  proofs: Map<string, boolean>;
}

/** Render-only occlusion. A complete solid tile rectangle must hide both the
 * conservative whole character envelope and its directional-light shadow.
 * Activity, collision, targeting and on-demand articulated poses are unchanged. */
export class InteriorOcclusion {
  private epoch = -1;
  private layers = new Map<string, Ceiling | null>();
  constructor(private physics: PhysicsCity) {}

  hidden(layer: string | undefined, p: Vec, scale: number, camera: Vec, light: Vec): boolean {
    if (!layer?.startsWith('interior:') || light.y <= 0) return false;
    if (this.epoch !== this.physics.life.epoch) {
      this.layers.clear();
      this.epoch = this.physics.life.epoch;
    }
    let ceiling = this.layers.get(layer);
    if (ceiling === undefined) {
      const [, id, floor] = layer.split(':'),
        building = this.physics.city.buildings.find((b) => b.id === Number(id)),
        spec = this.physics.city.interiors?.find((b) => b.buildingId === Number(id));
      ceiling =
        building && spec && !building.rotation
          ? { building, row: (Number(floor) + 1) * spec.floorHeight, version: -1, proofs: new Map() }
          : null;
      this.layers.set(layer, ceiling);
    }
    if (!ceiling || this.physics.buildingViewOpened(ceiling.building.id)) return false;
    const b = ceiling.building,
      row = ceiling.row,
      plane = (b.baseY ?? GROUND) + (row + 0.9) * CELL,
      bottom = p.y - 0.3 * scale,
      top = p.y + 2.5 * scale,
      radius = 1.6 * scale;
    if (plane <= top + 0.05 || camera.y <= plane + 0.05) return false;
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    for (const y of [bottom, top]) {
      const t = (plane - y) / (camera.y - y),
        lightT = (plane - y) / light.y;
      minX = Math.min(minX, (p.x - radius) * (1 - t) + camera.x * t, p.x - radius + light.x * lightT);
      maxX = Math.max(maxX, (p.x + radius) * (1 - t) + camera.x * t, p.x + radius + light.x * lightT);
      minZ = Math.min(minZ, (p.z - radius) * (1 - t) + camera.z * t, p.z - radius + light.z * lightT);
      maxZ = Math.max(maxZ, (p.z + radius) * (1 - t) + camera.z * t, p.z + radius + light.z * lightT);
    }
    const x0 = Math.floor((minX - b.x - 0.00001) / CELL),
      x1 = Math.floor((maxX - b.x + 0.00001) / CELL),
      z0 = Math.floor((minZ - b.z - 0.00001) / CELL),
      z1 = Math.floor((maxZ - b.z + 0.00001) / CELL);
    if (x0 < 0 || z0 < 0 || x1 >= b.w || z1 >= b.d || (x1 - x0 + 1) * (z1 - z0 + 1) > 100) return false;
    const version = this.physics.buildingSupportVersion(b.id);
    if (version !== ceiling.version) {
      ceiling.proofs.clear();
      ceiling.version = version;
    }
    const signature = `${x0}/${x1}/${z0}/${z1}`,
      cached = ceiling.proofs.get(signature);
    if (cached !== undefined) return cached;
    const first = b.id * 1e6 + key(x0, row, z0),
      owner = this.physics.cellOwner.get(first),
      active = this.physics.clusters.get(owner ?? -1),
      dormant = this.physics.dormant.get(owner ?? -1),
      record = active ?? dormant;
    let opaque = !!record?.anchored;
    if (record && opaque) {
      const q = active ? active.body.rotation() : dormant!.rotation,
        position = active ? active.body.translation() : dormant!.position;
      opaque =
        Math.abs(q.x) + Math.abs(q.y) + Math.abs(q.z) < 1e-8 &&
        Math.abs(position.x - record.origin.x - b.x) < 1e-6 &&
        Math.abs(position.y - record.origin.y - (b.baseY ?? GROUND)) < 1e-6 &&
        Math.abs(position.z - record.origin.z - b.z) < 1e-6;
      for (let z = z0; opaque && z <= z1; z++)
        for (let x = x0; x <= x1; x++) {
          const c = record.cells.get(b.id * 1e6 + key(x, row, z)),
            shape = c?.bounds;
          if (
            !c ||
            c.x !== x ||
            c.y !== row ||
            c.z !== z ||
            c.material === 'glass' ||
            c.material === 'foliage' ||
            c.parts ||
            (shape &&
              (shape.x > 0 ||
                shape.x + shape.w < 1 ||
                shape.z > 0 ||
                shape.z + shape.d < 1 ||
                shape.y > 0.9 ||
                shape.y + shape.h < 0.9))
          ) {
            opaque = false;
            break;
          }
        }
    }
    if (ceiling.proofs.size >= 128) ceiling.proofs.clear();
    ceiling.proofs.set(signature, opaque);
    return opaque;
  }
}
